/* ============================================================
 * Panasonic × Anker (miyotto) LP — アニメーションエンジン（下地）
 *
 * 方針:
 *  - GSAP / ScrollTrigger / Lenis は親セクションで CDN ロード（page-nebula-x1 と同版）。
 *  - 演出は「data属性ドリブン」。マークアップに属性を足すだけで動く＝JS非編集で修正可。
 *      [data-reveal]            … フェード+上昇（1回）。 任意: data-reveal-delay / -y
 *      [data-pin-seq]           … ピン留めクロスフェード列。子に [data-pin-scene]
 *      [data-filmstrip]         … ピン留め横スクロール。中に [data-filmstrip-track]
 *  - Shopifyテーマエディタ対応: shopify:section:load/unload で再init/破棄。
 *  - JS失敗 / reduced-motion 時は全要素を可視のままにする（フォールバック）。
 *
 * メンテのコツ: 各演出は「先頭の CONFIG を1行いじれば調整できる」ように関数化している。
 * ============================================================ */
(function () {
  "use strict";

  var REDUCE = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var DESKTOP_MQ = "(min-width: 1080px)"; // LP共通の単一ブレークポイント

  // GSAP未ロード or reduced-motion → 何も隠さず即表示して終了（フェイルセーフ）
  function bailOut(scope) {
    (scope || document)
      .querySelectorAll(".pana [data-reveal]")
      .forEach(function (el) {
        el.style.opacity = "1";
        el.style.transform = "none";
      });
  }
  if (typeof window.gsap === "undefined" || REDUCE) {
    document.documentElement.classList.remove("pana-js");
    document.addEventListener("DOMContentLoaded", function () {
      bailOut(document);
    });
    return;
  }

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  if (ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  /* ---- Lenis（グローバル1回だけ・二重init防止） + ScrollTrigger連携 ---- */
  function initLenis() {
    if (window.__panaLenis || typeof window.Lenis === "undefined") return;
    var lenis = new window.Lenis({ duration: 1.1, smoothWheel: true });
    window.__panaLenis = lenis;
    if (ScrollTrigger) {
      lenis.on("scroll", ScrollTrigger.update);
      gsap.ticker.add(function (t) {
        lenis.raf(t * 1000);
      });
      gsap.ticker.lagSmoothing(0);
    } else {
      function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);
    }
  }

  /* ============================================================
   *  演出1: data-reveal（フェード+上昇 / 1回）  ← 全セクションで使用
   * ============================================================ */
  function initReveal(scope) {
    gsap.utils.toArray(scope.querySelectorAll("[data-reveal]")).forEach(
      function (el) {
        if (el.__panaRevealed) return;
        el.__panaRevealed = true;
        var delay = parseFloat(el.getAttribute("data-reveal-delay")) || 0;
        var y = parseFloat(el.getAttribute("data-reveal-y"));
        if (isNaN(y)) y = 24;
        gsap.fromTo(
          el,
          { opacity: 0, y: y },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            delay: delay,
            ease: "expo.out",
            scrollTrigger: {
              trigger: el,
              start: "top 88%",
              toggleActions: "play none none none",
            },
          }
        );
      }
    );
  }

  /* ============================================================
   *  演出2: data-pin-seq（ピン留めクロスフェード列）
   *  例: POINT 01→02→03。子 [data-pin-scene] を等間隔でクロスフェード。
   *  CONFIG: data-scrub（既定0.6）。desktopのみ有効。
   *  ※ コンテンツ確定後、scene毎の scale など固有演出はここに追記する。
   * ============================================================ */
  function initPinSequence(scope) {
    gsap.utils.toArray(scope.querySelectorAll("[data-pin-seq]")).forEach(
      function (root) {
        if (root.__panaPinSeq) return;
        root.__panaPinSeq = true;
        var scrub = parseFloat(root.getAttribute("data-scrub"));
        if (isNaN(scrub)) scrub = 0.6;
        var stage = root.querySelector("[data-pin-stage]") || root;
        var scenes = gsap.utils.toArray(
          root.querySelectorAll("[data-pin-scene]")
        );
        if (scenes.length < 2) return;

        gsap.matchMedia().add(DESKTOP_MQ, function () {
          gsap.set(scenes, { opacity: 0 });
          gsap.set(scenes[0], { opacity: 1 });
          var tl = gsap.timeline({
            scrollTrigger: {
              trigger: root,
              start: "top top",
              end: "bottom bottom",
              scrub: scrub,
              pin: stage,
              pinSpacing: false,
              invalidateOnRefresh: true,
            },
          });
          for (var i = 1; i < scenes.length; i++) {
            var at = i / scenes.length;
            tl.to(scenes[i - 1], { opacity: 0, duration: 0.06, ease: "none" }, at)
              .to(scenes[i], { opacity: 1, duration: 0.06, ease: "none" }, at);
          }
        });
      }
    );
  }

  /* ============================================================
   *  演出3: data-filmstrip（ピン留め横スクロール）
   *  例: 使用シーンのフィルムストリップ。[data-filmstrip-track] を x 方向移動。
   *  CONFIG: data-scrub（既定0.6）。desktopのみ。invalidateOnRefreshで幅再計算。
   * ============================================================ */
  function initFilmstrip(scope) {
    gsap.utils.toArray(scope.querySelectorAll("[data-filmstrip]")).forEach(
      function (root) {
        if (root.__panaFilmstrip) return;
        root.__panaFilmstrip = true;
        var scrub = parseFloat(root.getAttribute("data-scrub"));
        if (isNaN(scrub)) scrub = 0.6;
        var track = root.querySelector("[data-filmstrip-track]");
        var stage = root.querySelector("[data-pin-stage]") || root;
        if (!track) return;

        gsap.matchMedia().add(DESKTOP_MQ, function () {
          gsap.fromTo(
            track,
            { x: function () { return window.innerWidth; } },
            {
              x: function () {
                return -track.scrollWidth + window.innerWidth * 0.5;
              },
              ease: "none",
              scrollTrigger: {
                trigger: root,
                start: "top top",
                end: "bottom bottom",
                scrub: scrub,
                pin: stage,
                pinSpacing: false,
                invalidateOnRefresh: true,
              },
            }
          );
        });
      }
    );
  }

  /* ============================================================
   *  上流セクション（Hero / Concept / ProductSplit / Points / UseCases）
   *  React版(components/*.tsx)の gsap.context 内ロジックを忠実移植。
   *  各 section の data-pana-* でゲートし、React と同じクラスフックを対象にする。
   * ============================================================ */
  function pickRoot(scope, sel) {
    if (scope.matches && scope.matches(sel)) return scope;
    return scope.querySelector ? scope.querySelector(sel) : null;
  }

  /* Hero KV: cover実測で円を画像にロック + イントロtl + アンビエント脈動 */
  function initHero(scope) {
    var root = pickRoot(scope, "[data-pana-hero]");
    if (!root || root.__panaHero) return;
    root.__panaHero = true;

    var bgwrap = root.querySelector(".hero-bgwrap");
    var stages = root.querySelectorAll(".hero-stage");
    var img = root.querySelector(".hero-bg");
    var circles = root.querySelectorAll(".hero-circle");
    var R = [0.1, 0.115], RING = 228 / 250, SCALE = 1.1;
    var nat = { w: 2400, h: 1793 };
    function sizeCover() {
      if (!bgwrap) return;
      var rect = bgwrap.getBoundingClientRect();
      var W = rect.width, H = rect.height;
      if (!W || !H) return;
      var A = nat.w / nat.h;
      var cw = W / H >= A ? W : H * A;
      var ch = W / H >= A ? W / A : H;
      stages.forEach(function (s) { s.style.width = cw + "px"; s.style.height = ch + "px"; });
      circles.forEach(function (c, i) {
        var oD = (R[i] || R[0]) * cw * SCALE, iD = oD * RING;
        var o = c.querySelector(".hero-ring-outer"), inr = c.querySelector(".hero-ring-inner");
        if (o) { o.style.width = oD + "px"; o.style.height = oD + "px"; o.style.left = -oD / 2 + "px"; o.style.top = -oD / 2 + "px"; }
        if (inr) { inr.style.width = iD + "px"; inr.style.height = iD + "px"; inr.style.left = -iD / 2 + "px"; inr.style.top = -iD / 2 + "px"; }
      });
    }
    if (img) {
      if (img.complete && img.naturalWidth) nat = { w: img.naturalWidth, h: img.naturalHeight };
      img.addEventListener("load", function () {
        if (img.naturalWidth) { nat = { w: img.naturalWidth, h: img.naturalHeight }; sizeCover(); if (ScrollTrigger) ScrollTrigger.refresh(); }
      });
    }
    sizeCover();
    window.addEventListener("resize", sizeCover);

    gsap.matchMedia().add("(min-width: 1080px)", function () {
      gsap.set(".hero-copy-line", { clipPath: "inset(100% 0 0 0)" });
      gsap.set(".hero-glow", { scale: 0, filter: "blur(40px)" });
      gsap.set(".hero-pill", { y: 24, opacity: 0 });
      gsap.set(".hero-connector", { opacity: 0 });
      gsap.set(".hero-logo", { y: -8, opacity: 0 });
      gsap.set(".hero-stage", { scale: 1.15 });
      gsap.set(".hero-bg", { opacity: 0 });
      var tl = gsap.timeline({ defaults: { ease: "expo.out" } });
      tl.to(".hero-bg", { opacity: 1, duration: 1.2 }, 0)
        .to(".hero-stage", { scale: 1.05, duration: 1.2 }, 0)
        .to(".hero-stage", { scale: 1.0, duration: 12, ease: "none" }, 0)
        .to(".hero-logo", { y: 0, opacity: 1, duration: 0.6, ease: "power2.out" }, 0.4)
        .to(".hero-glow", { scale: 1, filter: "blur(0px)", duration: 0.9, stagger: 0.15, ease: "power2.out" }, 0.6)
        .to(".hero-pill", { y: 0, opacity: 1, duration: 0.7, stagger: 0.15, ease: "power2.out" }, 0.9)
        .to(".hero-connector", { opacity: 1, duration: 0.5 }, 0.95)
        .to(".hero-copy-line", { clipPath: "inset(0% 0 0 0)", duration: 0.9, stagger: 0.12 }, 1.2);
      gsap.to(".hero-glow-1, .hero-glow-3", { scale: 1.06, boxShadow: "0 0 30px rgba(255,255,255,0.45), 0 0 60px rgba(255,255,255,0.2)", duration: 3.2, repeat: -1, yoyo: true, ease: "sine.inOut", delay: 2.0 });
      gsap.to(".hero-glow-2, .hero-glow-4", { scale: 1.03, boxShadow: "0 0 24px rgba(255,255,255,0.55), 0 0 48px rgba(255,255,255,0.25)", duration: 3.2, repeat: -1, yoyo: true, ease: "sine.inOut", delay: 3.6 });
    });
    gsap.matchMedia().add("(max-width: 1079px)", function () {
      gsap.set(".kvm-copy", { clipPath: "inset(100% 0 0 0)" });
      gsap.set(".kvm-glow", { scale: 0, filter: "blur(20px)" });
      var tl = gsap.timeline({ defaults: { ease: "power2.out" } });
      tl.from(".kvm-logo", { y: -8, opacity: 0, duration: 0.6 }, 0.2)
        .to(".kvm-glow", { scale: 1, filter: "blur(0px)", duration: 0.9, stagger: 0.15 }, 0.5)
        .from(".kvm-pill", { y: 24, opacity: 0, duration: 0.7, stagger: 0.12 }, 0.7)
        .to(".kvm-copy", { clipPath: "inset(0% 0 0 0)", duration: 0.9, stagger: 0.12, ease: "expo.out" }, 1.0);
      gsap.to(".kvm-glow-1, .kvm-glow-3", { scale: 1.06, boxShadow: "0 0 15px rgba(255,255,255,0.45), 0 0 30px rgba(255,255,255,0.2)", duration: 3.2, repeat: -1, yoyo: true, ease: "sine.inOut", delay: 2.0 });
      gsap.to(".kvm-glow-2, .kvm-glow-4", { scale: 1.03, boxShadow: "0 0 12px rgba(255,255,255,0.55), 0 0 24px rgba(255,255,255,0.25)", duration: 3.2, repeat: -1, yoyo: true, ease: "sine.inOut", delay: 3.6 });
    });
  }

  /* Concept: 画像maskリビール + 行リビール + scrubズーム（右カラム固定はCSS sticky） */
  function initConcept(scope) {
    var root = pickRoot(scope, "[data-pana-concept]");
    if (!root || root.__panaConcept) return;
    root.__panaConcept = true;
    gsap.matchMedia().add("(max-width: 1079px)", function () {
      gsap.fromTo(".csm-product", { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: "expo.out", scrollTrigger: { trigger: ".csm-product", start: "top 85%", toggleActions: "play none none none" } });
      gsap.utils.toArray(".csm-line").forEach(function (line, i) {
        gsap.fromTo(line, { opacity: 0.2, y: 8 }, { opacity: 1, y: 0, duration: 0.5, delay: i * 0.06, ease: "power2.out", scrollTrigger: { trigger: ".csm-text", start: "top 80%", toggleActions: "play none none none" } });
      });
      gsap.utils.toArray(".csm-img").forEach(function (wrap) {
        var im = wrap.querySelector("img");
        gsap.fromTo(wrap, { clipPath: "inset(12% 18% 12% 18%)" }, { clipPath: "inset(0% 0% 0% 0%)", duration: 1.2, ease: "expo.out", scrollTrigger: { trigger: wrap, start: "top 85%", toggleActions: "play none none none" } });
        if (im) gsap.fromTo(im, { scale: 1.08 }, { scale: 1.0, duration: 1.6, ease: "expo.out", scrollTrigger: { trigger: wrap, start: "top 85%", toggleActions: "play none none none" } });
      });
    });
    gsap.matchMedia().add("(min-width: 1080px)", function () {
      gsap.utils.toArray(".concept-img").forEach(function (wrap) {
        var im = wrap.querySelector("img");
        gsap.fromTo(wrap, { clipPath: "inset(15% 30% 15% 30%)" }, { clipPath: "inset(0% 0% 0% 0%)", duration: 1.2, ease: "expo.out", scrollTrigger: { trigger: wrap, start: "top 80%", toggleActions: "play none none none" } });
        if (im) gsap.fromTo(im, { scale: 1.08 }, { scale: 1.0, duration: 1.6, ease: "expo.out", scrollTrigger: { trigger: wrap, start: "top 80%", toggleActions: "play none none none" } });
      });
      gsap.utils.toArray(".concept-line").forEach(function (line, i) {
        gsap.fromTo(line, { opacity: 0.3, y: 10 }, { opacity: 1, y: 0, duration: 0.6, delay: i * 0.08, ease: "power2.out", scrollTrigger: { trigger: ".concept-text", start: "top 75%", toggleActions: "play none none none" } });
      });
      gsap.fromTo(".concept-product", { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: "expo.out", scrollTrigger: { trigger: ".concept-product", start: "top 85%", toggleActions: "play none none none" } });
      // 右カラムの固定は CSS sticky（.pana-concept__sticky）に移行。
      // ScrollTrigger.pin だと reduced-motion / GSAP未ロード時に sticky ごと消えるため。
      gsap.fromTo(".concept-product", { scale: 0.95 }, { scale: 1.05, ease: "none", scrollTrigger: { trigger: root, start: "top top", end: "bottom bottom", scrub: 0.8 } });
    });
  }

  /* ProductSplit: 左右パネルスライドイン + 中身フェード + 視差 */
  function initProductSplit(scope) {
    var root = pickRoot(scope, "[data-pana-productsplit]");
    if (!root || root.__panaPS) return;
    root.__panaPS = true;
    gsap.fromTo(".ps-panel-left", { xPercent: -100 }, { xPercent: 0, duration: 1.0, ease: "expo.out", scrollTrigger: { trigger: root, start: "top 75%", toggleActions: "play none none none" } });
    gsap.fromTo(".ps-panel-right", { xPercent: 100 }, { xPercent: 0, duration: 1.0, delay: 0.1, ease: "expo.out", scrollTrigger: { trigger: root, start: "top 75%", toggleActions: "play none none none" } });
    gsap.utils.toArray(".ps-panel").forEach(function (panel) {
      var im = panel.querySelector(".ps-image"), lines = panel.querySelectorAll(".ps-line"), cta = panel.querySelector(".ps-cta");
      var tl = gsap.timeline({ scrollTrigger: { trigger: root, start: "top 75%", toggleActions: "play none none none" }, delay: 0.5 });
      if (im) tl.fromTo(im, { scale: 0.92, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.8, ease: "expo.out" }, 0);
      tl.fromTo(lines, { y: 16, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: "power2.out" }, 0.2);
      if (cta) tl.fromTo(cta, { opacity: 0 }, { opacity: 1, duration: 0.5, ease: "power2.out" }, 0.6);
    });
    gsap.matchMedia().add("(min-width: 1080px)", function () {
      gsap.to(".ps-image", { y: -20, ease: "none", scrollTrigger: { trigger: root, start: "top bottom", end: "bottom top", scrub: true } });
    });
  }

  /* PointSequence: pin クロスフェード列 + scene毎 scale
   * ★desktopのみ。スマホはピン留め(stay)なし＝CSSが通常フローで縦積みする */
  function initPoints(scope) {
    var root = pickRoot(scope, "[data-pana-points]");
    if (!root || root.__panaPoints) return;
    root.__panaPoints = true;
    gsap.matchMedia().add(DESKTOP_MQ, function () {
      gsap.set([".point-scene-1", ".point-scene-2"], { opacity: 0 });
      gsap.set([".point-image-0", ".point-image-1", ".point-image-2"], { scale: 1.0 });
      var tl = gsap.timeline({ scrollTrigger: { trigger: root, start: "top top", end: "bottom bottom", scrub: 0.6, pin: ".point-stage", pinSpacing: false, invalidateOnRefresh: true } });
      tl.to(".point-image-0", { scale: 1.05, ease: "none", duration: 0.3 }, 0);
      tl.to(".point-scene-0", { opacity: 0, ease: "none", duration: 0.06 }, 0.3).to(".point-scene-1", { opacity: 1, ease: "none", duration: 0.06 }, 0.3);
      tl.fromTo(".point-image-1", { scale: 1.0 }, { scale: 1.05, ease: "none", duration: 0.3 }, 0.36);
      tl.to(".point-scene-1", { opacity: 0, ease: "none", duration: 0.06 }, 0.66).to(".point-scene-2", { opacity: 1, ease: "none", duration: 0.06 }, 0.66);
      tl.fromTo(".point-image-2", { scale: 1.0 }, { scale: 1.05, ease: "none", duration: 0.23 }, 0.72);
      tl.to(".point-scene-2", { opacity: 0, ease: "none", duration: 0.05 }, 0.95);
    });
    /* スマホ: pinなしでも「スクロールで写真がズーム」だけ残す。
       各sceneが画面を通過する間に 1.0→1.05（PCと同倍率・scrub連動） */
    gsap.matchMedia().add("(max-width: 1079px)", function () {
      gsap.utils.toArray(root.querySelectorAll(".point-image")).forEach(function (img) {
        var scene = img.closest ? img.closest(".point-scene") : null;
        gsap.fromTo(img, { scale: 1.0 }, {
          scale: 1.05,
          ease: "none",
          scrollTrigger: { trigger: scene || img, start: "top bottom", end: "bottom top", scrub: 0.6, invalidateOnRefresh: true },
        });
      });
    });
  }

  /* UseCases: pin 横スクロール filmstrip + 幅キー縮尺 + sub-label切替 */
  function initUseCases(scope) {
    var root = pickRoot(scope, "[data-pana-usecases]");
    if (!root || root.__panaUC) return;
    root.__panaUC = true;
    var BLOCK_H = 923;
    ScrollTrigger.config({ ignoreMobileResize: true });
    var filmstrip = root.querySelector(".uc-filmstrip"), stage = root.querySelector(".uc-stage"), textwrap = root.querySelector(".uc-textwrap");
    if (!filmstrip || !stage) return;
    var cachedW = -1, cachedScale = 1;
    function getScale() {
      var vw = window.innerWidth;
      if (vw === cachedW) return cachedScale;
      cachedW = vw;
      if (vw >= 1080) { cachedScale = 1; return 1; }
      var vh = window.innerHeight, sH = (vh * 0.88) / BLOCK_H, sW = (vw - 32) / 520;
      cachedScale = Math.min(1, sH, sW);
      return cachedScale;
    }
    function applyScale() {
      var S = getScale();
      gsap.set(filmstrip, { scale: S, transformOrigin: "left center" });
      if (textwrap) gsap.set(textwrap, { scale: S, transformOrigin: "left top" });
    }
    applyScale();
    ScrollTrigger.addEventListener("refreshInit", applyScale);
    /* 初期位置: 写真1(left:185px/幅840px)が右端から「半分」見えた状態で開始する。
       605 = 185 + 840/2。冒頭カット削除で生じた長い暗転を避ける。 */
    gsap.fromTo(filmstrip, { x: function () { return window.innerWidth - 605 * getScale(); } }, {
      x: function () { return -filmstrip.scrollWidth * getScale() + window.innerWidth * 0.5; },
      ease: "none",
      scrollTrigger: { trigger: root, start: "top top", end: "bottom bottom", scrub: 0.6, pin: stage, pinSpacing: false, invalidateOnRefresh: true },
    });
    gsap.set(".uc-sublabel-drama", { opacity: 0, y: 18 });
    gsap.set(".uc-sublabel-oshikatsu", { opacity: 1, y: 0 });
    var labelTl = gsap.timeline({ scrollTrigger: { trigger: root, start: "top top", end: "bottom bottom", scrub: 0.3 } });
    labelTl.to(".uc-sublabel-oshikatsu", { y: -18, opacity: 0, ease: "none", duration: 0.02 }, 0.02)
      .to(".uc-sublabel-drama", { y: 0, opacity: 1, ease: "none", duration: 0.02 }, 0.03);
    /* セクション終端: 次セクション(recommend)が pinSpacing:false でピン領域へせり上がり、
       まだ固定中の sticky テキストを下から覆って見切れさせる。直前(終盤~1/3)で
       opacity をフェードして文字を消し、見切れを防ぐ。既存フックには触れず独立 timeline。 */
    var stickytext = root.querySelector(".uc-stickytext");
    if (stickytext) {
      var fadeTl = gsap.timeline({ scrollTrigger: { trigger: root, start: "top top", end: "bottom bottom", scrub: 0.6 } });
      fadeTl.to(stickytext, { opacity: 1, ease: "none", duration: 0.66 })
        .to(stickytext, { opacity: 0, ease: "none", duration: 0.3 });
    }
    var lastW = window.innerWidth;
    window.addEventListener("resize", function () {
      if (window.innerWidth === lastW) return;
      lastW = window.innerWidth;
      ScrollTrigger.refresh();
    });
  }

  /* ---- 固定ヘッダー: ロゴバーを body 直下へ退避して追従を保証 ----
   *  祖先 .page-container は overflow:clip / 各種コンテインで position:fixed の
   *  追従が効かない（テーマ本体のヘッダーも page-container の外側に置かれている）。
   *  そのため CSS だけでは固定できず、ロゴバーを body へ移設して viewport 基準にする。
   *  移設後の表示切替(PC/SP)は親 display ではなく bar 自身のメディアクエリが担当。
   *  ★Anker共通ヘッダー(.v2-header z-index:999 等)より必ず背面にするため、
   *    z-index(31<999)に加えて body の「先頭」へ挿入し DOM順でも背面に回す。
   *    （共通ヘッダーCSS未適用=z-index auto の環境でも前面に出さない保険）
   */
  function initStickyHeader() {
    var bars = document.querySelectorAll(
      ".pana-hero__logobar, .pana-hero__mlogobar"
    );
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      if (bar.__panaPortaled) continue;
      bar.__panaPortaled = true;
      document.body.insertBefore(bar, document.body.firstChild);
    }
    trackSiteHeader();
  }

  /* Anker共通ヘッダー(.js-v2-header-sticky)の「下端」を追跡し、LPロゴバーの
   * top(--pana-lpbar-top)をその直下へ合わせる。初期表示では共通ヘッダーの
   * すぐ下、スクロールで共通ヘッダーが隠れたら top:0 に詰める(PC/SP共通)。
   * 共通ヘッダーは top の transition(0.2s) でshow/hideするため、scroll/resize/
   * class変化を起点に少しの間 rAF で実測追従する。 */
  function trackSiteHeader() {
    if (window.__panaLpBarTracked) return;
    window.__panaLpBarTracked = true;
    var siteHeader = document.querySelector(".js-v2-header-sticky");
    if (!siteHeader) return;

    var lastTop = -1;
    function applyTop() {
      var bottom = Math.max(
        0,
        Math.round(siteHeader.getBoundingClientRect().bottom)
      );
      if (bottom === lastTop) return;
      lastTop = bottom;
      document.documentElement.style.setProperty(
        "--pana-lpbar-top",
        bottom + "px"
      );
    }

    var rafId = null;
    var rafUntil = 0;
    function tick(now) {
      applyTop();
      if (now < rafUntil) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    }
    function kick() {
      rafUntil = performance.now() + 350; // transition 0.2s + 余裕
      if (rafId === null) rafId = requestAnimationFrame(tick);
    }

    window.addEventListener("scroll", kick, { passive: true });
    window.addEventListener("resize", kick);
    new MutationObserver(kick).observe(siteHeader, {
      attributes: true,
      attributeFilter: ["class"],
    });
    applyTop();
  }

  /* ---- まとめて初期化 / scope単位で破棄（エディタ再描画対応） ---- */
  function initAll(scope) {
    scope = scope || document;
    if (!scope.querySelector || !scope.querySelector(".pana")) {
      if (!(scope.classList && scope.classList.contains("pana"))) {
        // scope自体が .pana を含まなければ何もしない
        if (!scope.querySelector(".pana")) return;
      }
    }
    initLenis();
    initStickyHeader();
    initReveal(scope);
    initPinSequence(scope);
    initFilmstrip(scope);
    initHero(scope);
    initConcept(scope);
    initProductSplit(scope);
    initPoints(scope);
    initUseCases(scope);
    if (ScrollTrigger) ScrollTrigger.refresh();
  }

  function killIn(scope) {
    if (!ScrollTrigger) return;
    ScrollTrigger.getAll().forEach(function (st) {
      var trg = st.trigger;
      if (trg && scope.contains && scope.contains(trg)) st.kill();
    });
  }

  /* ---- 起動 + Shopifyテーマエディタのライフサイクル ---- */
  document.addEventListener("DOMContentLoaded", function () {
    initAll(document);
  });
  // 画像/フォント確定後に座標再計算
  window.addEventListener("load", function () {
    if (ScrollTrigger) ScrollTrigger.refresh();
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      if (ScrollTrigger) ScrollTrigger.refresh();
    });
  }
  // テーマカスタマイザー: セクション再描画時に再init / 破棄
  document.addEventListener("shopify:section:load", function (e) {
    initAll(e.target);
  });
  document.addEventListener("shopify:section:unload", function (e) {
    killIn(e.target);
  });
  document.addEventListener("shopify:section:select", function () {
    if (ScrollTrigger) ScrollTrigger.refresh();
  });
})();
